/**
 * Shopify Storefront API Utilities
 * Helper functions for token management, localStorage of accessTokens
 */

const ShopifyUtils = {
  /**
   * Storage keys
   */
  STORAGE_KEYS: {
    CUSTOMER_TOKEN: 'shopifyCustomerAccessToken',
    TOKEN_EXPIRES: 'shopifyCustomerAccessTokenExpires',
    CART_ID: 'shopifyCartId'
  },

  /**
   * Check if customer access token is expired
   * @param {string} expiresAt - ISO date string
   * @returns {boolean}
   */
  isTokenExpired(expiresAt) {
    if (!expiresAt) return true;
    const expirationDate = new Date(expiresAt);
    const now = new Date();
    // Subtract 1 hour buffer for safety
    expirationDate.setHours(expirationDate.getHours() - 1);
    return now >= expirationDate;
  },

  /**
   * Get customer access token from localStorage
   * @returns {string|null}
   */
  getCustomerToken() {
    const token = localStorage.getItem(this.STORAGE_KEYS.CUSTOMER_TOKEN);
    const expiresAt = localStorage.getItem(this.STORAGE_KEYS.TOKEN_EXPIRES);
    
    if (!token) return null;
    
    // Check if expired
    if (this.isTokenExpired(expiresAt)) {
      this.clearCustomerToken();
      return null;
    }
    
    return token;
  },

  /**
   * Set customer access token in localStorage
   * @param {string} token - Access token
   * @param {string} expiresAt - ISO date string
   */
  setCustomerToken(token, expiresAt) {
    localStorage.setItem(this.STORAGE_KEYS.CUSTOMER_TOKEN, token);
    localStorage.setItem(this.STORAGE_KEYS.TOKEN_EXPIRES, expiresAt);
  },

  /**
   * Clear customer access token from localStorage
   */
  clearCustomerToken() {
    localStorage.removeItem(this.STORAGE_KEYS.CUSTOMER_TOKEN);
    localStorage.removeItem(this.STORAGE_KEYS.TOKEN_EXPIRES);
  },

  /**
   * Get cart ID from localStorage
   * @returns {string|null}
   */
  getCartId() {
    return localStorage.getItem(this.STORAGE_KEYS.CART_ID);
  },

  /**
   * Set cart ID in localStorage
   * @param {string} cartId - Cart ID
   */
  setCartId(cartId) {
    localStorage.setItem(this.STORAGE_KEYS.CART_ID, cartId);
  },

  /**
   * Clear cart ID from localStorage
   */
  clearCartId() {
    localStorage.removeItem(this.STORAGE_KEYS.CART_ID);
  },

  /**
   * Extract error messages from GraphQL response
   * @param {Object} response - GraphQL response
   * @returns {string[]}
   */
  extractErrors(response) {
    const errors = [];
    
    if (response.errors) {
      response.errors.forEach(error => {
        errors.push(error.message || 'An error occurred');
      });
    }
    
    // Check for userErrors in mutations
    const userErrors = response.data?.customerUserErrors || 
                      response.data?.userErrors ||
                      response.data?.customerAccessTokenCreate?.customerUserErrors ||
                      response.data?.customerCreate?.customerUserErrors ||
                      response.data?.customerRecover?.customerUserErrors ||
                      response.data?.cartCreate?.userErrors ||
                      response.data?.cartLinesAdd?.userErrors ||
                      response.data?.cartLinesRemove?.userErrors ||
                      response.data?.cartLinesUpdate?.userErrors ||
                      response.data?.customerAddressCreate?.customerUserErrors ||
                      response.data?.customerAddressUpdate?.customerUserErrors ||
                      response.data?.customerAddressDelete?.customerUserErrors ||
                      response.data?.customerDefaultAddressUpdate?.customerUserErrors;
    
    if (userErrors && Array.isArray(userErrors)) {
      userErrors.forEach(error => {
        errors.push(error.message || 'An error occurred');
      });
    }
    
    return errors.length > 0 ? errors : ['An unexpected error occurred'];
  },

  /**
   * Format error message for display
   * @param {Object} response - GraphQL response
   * @returns {string}
   */
  formatErrorMessage(response) {
    const errors = this.extractErrors(response);
    return errors.join('. ');
  },

  /**
   * Check if customer is logged in
   * Considers both headless auth (localStorage token) AND native Shopify session
   * This unified approach handles cases where one auth state exists without the other
   * @returns {boolean}
   */
  isCustomerLoggedIn() {
    // Check headless auth token (localStorage)
    const token = this.getCustomerToken();
    if (token) {
      return true;
    }
    
    // Also check native Shopify session (set by Liquid {% if customer %})
    // This handles cases where localStorage was cleared but native session exists
    if (window.isCustomerLoggedIn === true || window.isCustomerLoggedIn === 'true') {
      return true;
    }
    
    return false;
  },

  /**
   * Check if customer is logged in via headless auth specifically
   * Use this when you need to know if we have a Storefront API token
   * @returns {boolean}
   */
  isHeadlessAuthLoggedIn() {
    const token = this.getCustomerToken();
    return token !== null;
  },

  /**
   * Check if customer is logged in via native Shopify session
   * Use this when you need to know if Liquid {% if customer %} would be true
   * @returns {boolean}
   */
  isNativeSessionLoggedIn() {
    return window.isCustomerLoggedIn === true || window.isCustomerLoggedIn === 'true';
  },

  /**
   * Check if response has errors
   * @param {Object} response - GraphQL response
   * @returns {boolean}
   */
  hasErrors(response) {
    if (response.errors && response.errors.length > 0) return true;
    
    const userErrors = response.data?.customerUserErrors || 
                      response.data?.userErrors ||
                      response.data?.customerAccessTokenCreate?.customerUserErrors ||
                      response.data?.customerCreate?.customerUserErrors ||
                      response.data?.customerRecover?.customerUserErrors ||
                      response.data?.cartCreate?.userErrors ||
                      response.data?.cartLinesAdd?.userErrors ||
                      response.data?.cartLinesRemove?.userErrors ||
                      response.data?.cartLinesUpdate?.userErrors ||
                      response.data?.customerAddressCreate?.customerUserErrors ||
                      response.data?.customerAddressUpdate?.customerUserErrors ||
                      response.data?.customerAddressDelete?.customerUserErrors ||
                      response.data?.customerDefaultAddressUpdate?.customerUserErrors;
    
    return userErrors && userErrors.length > 0;
  }
};

// Export for use in other modules
if (typeof window !== 'undefined') {
  window.ShopifyUtils = ShopifyUtils;
}




